// biome-ignore-all lint/performance/noBarrelFile: Package entrypoint exposes the Antigravity adapter contract.
import { bridgeAgentControllerConversationRef, buildExecutableLaunchContract, createSharedControlShell, createSharedDispatchShell, invokeWithSingleContextHeal, resolveBuiltinAdapterConversationBridgeSpec, signalAgentControllerProcessTree, } from "@openmeld/adapter-core";
import { verifyAntigravityLaunchContract as verifyAntigravityLaunchContractLocal } from "./config/antigravity-runtime-config.js";
import { AntigravityCliTransportError, invokeAntigravityCliTransport, } from "./transport/antigravity-cli-transport.js";
import { withAntigravityDispatchSpaceAction } from "./transport/antigravity-space-action-plugin.js";
const ANTIGRAVITY_CONVERSATION_BRIDGE_SPEC = resolveBuiltinAdapterConversationBridgeSpec("antigravity");
export { resolveInstalledAntigravityLaunchCandidates, verifyAntigravityLaunchContract, } from "./config/antigravity-runtime-config.js";
export { parseAntigravityOutputLine } from "./output/antigravity-output-parser.js";
export { AntigravityCliTransportError, buildAntigravityArgs, invokeAntigravityCliTransport, } from "./transport/antigravity-cli-transport.js";
export { parseAntigravityUsageLimits } from "./usage/antigravity-usage-limits.js";
export function createAntigravityAdapter(options = {}) {
    const activeTasks = new Map();
    const invoke = createDefaultAntigravityInvoke({
        activeTasks,
        options,
    });
    const processInterruptSender = options.registerRuntimeProcess
        ? createProcessInterruptSender(activeTasks)
        : undefined;
    const controlShell = createSharedControlShell({
        gracefulTimeoutMs: options.gracefulTimeoutMs,
        sendCancel: options.sendCancel,
        sendInterrupt: options.sendInterrupt ?? processInterruptSender,
        waitForMs: options.waitForMs,
    });
    const dispatch = createSharedDispatchShell({
        adapterId: "antigravity",
        bridgeSpec: ANTIGRAVITY_CONVERSATION_BRIDGE_SPEC,
        invoke: options.invoke,
        invokeByMode: options.invokeByMode,
        repairConversationPolicy: "require_canonical",
        resolveDefaultInvoke: () => invoke,
        runtimeOptions: options,
    });
    return {
        adapterId: "antigravity",
        cancel: controlShell.cancel,
        dispatch,
        interrupt: controlShell.interrupt,
        verifyComputerFileAccessBoundary: () => Promise.resolve({
            reasonCode: "computer.file_access.controller_unsupported",
            supported: false,
        }),
    };
}
function createDefaultAntigravityInvoke(input) {
    return async (request) => {
        const task = {
            cancellationRequested: false,
            process: null,
        };
        input.activeTasks.set(request.taskId, task);
        try {
            const launchContract = await resolveDispatchLaunchContract({
                antigravityBin: input.options.antigravityBin,
                inputLaunchContract: request.launchContract,
                optionLaunchContract: input.options.launchContract,
            });
            if (task.cancellationRequested) {
                throw new AntigravityCliTransportError({
                    code: "runtime.cancelled_before_start",
                    errorType: "adapter",
                    message: "runtime.cancelled_before_start: Antigravity dispatch was canceled before launch",
                });
            }
            const contextId = normalizeOptionalText(request.agentControllerConversationId);
            const result = await invokeWithSingleContextHeal({
                contextId,
                contextLossPolicy: request.executionMode === "repair" ? "fail" : "rebuild",
                createRebuildFailedError: (error) => new AntigravityCliTransportError({
                    code: "context.rebuild_failed",
                    errorType: "adapter",
                    message: `context.rebuild_failed: ${toErrorMessage(error)}`,
                }),
                invokeOnce: async (sessionId) => {
                    const transportResult = await withAntigravityDispatchSpaceAction({
                        env: request.env,
                        run: async ({ addDir, jsonSchema }) => await invokeAntigravityCliTransport({
                            addDir,
                            conversationPolicy: request.conversationPolicy,
                            env: request.env,
                            jsonSchema,
                            launchContract,
                            onPreview: request.onPreview,
                            onProcessControlEvent: (event) => {
                                task.process =
                                    event.phase === "process_spawned" ? event : null;
                                if (event.phase === "process_spawned" &&
                                    task.cancellationRequested) {
                                    signalProcess(event, "SIGINT");
                                }
                                input.options.registerRuntimeProcess?.({
                                    ...event,
                                    taskId: request.taskId,
                                });
                            },
                            onRuntimeEvent: request.onRuntimeEvent,
                            prompt: request.prompt,
                            sessionId,
                            timeoutMs: request.timeoutMs ?? null,
                            workdir: request.runtimeContext.providerRuntimeWorkingDirectory,
                        }),
                    });
                    return {
                        contextId: transportResult.sessionId,
                        output: transportResult.output,
                        telemetry: transportResult.telemetry,
                    };
                },
                isContextLostError: (error) => error instanceof AntigravityCliTransportError &&
                    error.code === "context_not_found",
            });
            const agentControllerConversation = ANTIGRAVITY_CONVERSATION_BRIDGE_SPEC && result.contextId
                ? bridgeAgentControllerConversationRef({
                    bridgeSpec: ANTIGRAVITY_CONVERSATION_BRIDGE_SPEC,
                    agentControllerConversationId: result.contextId,
                })
                : null;
            return {
                contextDelta: {
                    agentControllerConversationId: result.contextId,
                    refreshed: result.contextHealed,
                    ...(agentControllerConversation
                        ? { agentControllerConversation }
                        : {}),
                },
                output: result.output,
                telemetry: result.telemetry,
            };
        }
        finally {
            if (input.activeTasks.get(request.taskId) === task) {
                input.activeTasks.delete(request.taskId);
            }
        }
    };
}
async function resolveDispatchLaunchContract(input) {
    const commandPath = normalizeOptionalText(input.antigravityBin);
    const launchContract = input.inputLaunchContract ??
        input.optionLaunchContract ??
        (commandPath ? buildExecutableLaunchContract({ commandPath }) : null);
    if (!launchContract) {
        throw Object.assign(new Error("Antigravity CLI launch contract is required for dispatch."), { code: "controller.launch.needs_repair", errorType: "config" });
    }
    const verification = await verifyAntigravityLaunchContractLocal({
        launchContract,
    });
    if (!verification.ok) {
        throw Object.assign(new Error(verification.errorMessage), {
            code: "controller.launch.needs_repair",
            errorType: "config",
        });
    }
    return launchContract;
}
function createProcessInterruptSender(activeTasks) {
    return (signal) => {
        const task = activeTasks.get(signal.taskId);
        if (!task) {
            return Promise.reject(Object.assign(new Error("Antigravity task is not running."), {
                code: "task.not_running",
            }));
        }
        task.cancellationRequested = true;
        if (!task.process) {
            return Promise.resolve();
        }
        const sent = signalProcess(task.process, signal.mode === "force" ? "SIGKILL" : "SIGINT");
        return sent
            ? Promise.resolve()
            : Promise.reject(new Error("Antigravity process signal was not sent."));
    };
}
function signalProcess(processEvent, signal) {
    return signalAgentControllerProcessTree({
        processId: processEvent.processId,
        signal,
        signalTarget: processEvent.signalTarget,
    });
}
function normalizeOptionalText(value) {
    const normalized = String(value ?? "").trim();
    return normalized.length > 0 ? normalized : null;
}
function toErrorMessage(error) {
    return error instanceof Error ? error.message : String(error);
}
